<script setup lang="ts">
/**
 * Espace client « Mes factures » — vue company-wide (régime invoice-only).
 *
 * Liste À PLAT de toutes les factures de l'organisation (déposées + Chorus),
 * avec recherche plein-texte + filtres cumulables (fournisseur, année, statut)
 * via `FacturesFiltres` (DEV-800). La vision par compteur reste au niveau source
 * (`PageSourceFactures`). AUCUNE anomalie / verdict / économie (frontière R1,
 * SPEC §11).
 *
 * Tout passe par `store.fetchList(query)` (pagination curseur conservée) →
 * `GET /v5/energyinvoices` (api-metier), ou fixtures `VITE_USE_MOCK_FACTURES=true`.
 */
import { computed, onMounted, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import FacturesFiltres from "@modules/courtage/components/factures/FacturesFiltres.vue"
import FacturesList from "@modules/courtage/components/factures/FacturesList.vue"
import ModalAjouterCompte from "@modules/courtage/components/factures/ModalAjouterCompte.vue"
import ModalDeposerFactures from "@modules/courtage/components/factures/ModalDeposerFactures.vue"
import { useEspaceFacturesStore } from "@modules/courtage/stores/espace-factures.store"
import type {
    FactureItem,
    FacturesQuery,
    FactureUploadJob,
} from "@modules/courtage/types/facture-espace"

defineOptions({ name: "PageMesFactures" })

const store = useEspaceFacturesStore()
const { factures, agregats, nextCursor, loading, loadingMore, error } =
    storeToRefs(store)

// Filtre de la liste à plat (recherche + facettes). Tri par défaut date desc.
// Tout passe par `store.fetchList(query)` ; la pagination curseur est conservée.
const query = ref<FacturesQuery>({ sort: "date", order: "desc" })

const hasActiveFilters = computed<boolean>(() =>
    Boolean(
        query.value.q ||
            query.value.fournisseur ||
            query.value.statut ||
            query.value.depuis ||
            query.value.jusqua
    )
)

// Action unique « Ajouter à mon compte » : aperçu (la demande de consentement
// réelle est envoyée séparément — aucune écriture ici).
const aAjouter = ref<FactureItem | null>(null)
const confirmation = ref<string | null>(null)
const deposerOuvert = ref(false)

onMounted(() => {
    void store.fetchList(query.value)
})

// Le composant de filtres remplace `query` par un nouvel objet à chaque
// changement → on recharge la liste (curseur réinitialisé côté store).
watch(
    query,
    (q) => {
        void store.fetchList(q)
    },
    { deep: true }
)

const nbAVerrouiller = computed(
    () =>
        (agregats.value?.n_a_rattacher ?? 0) +
        (agregats.value?.n_a_ajouter ?? 0)
)

function onVoir(f: FactureItem): void {
    // Ouvre le scan original (URL présignée via api-metier → courtage).
    void store.openPdf(f)
}

function onCta(f: FactureItem): void {
    // Une seule décision : ouvre l'aperçu « Ajouter à mon compte ».
    confirmation.value = null
    aAjouter.value = f
}

function onConfirmAjout(f: FactureItem): void {
    // Aperçu seulement : on note la demande côté UI (le consentement réel est
    // déclenché séparément — pas d'action sortante depuis l'espace).
    aAjouter.value = null
    confirmation.value = `Demande d'ajout notée pour le compteur ${f.pdl}. Nous revenons vers vous pour la signature du consentement.`
}

function onDeposer(): void {
    confirmation.value = null
    deposerOuvert.value = true
}

function onDepotFini(job: FactureUploadJob): void {
    // La liste est déjà rafraîchie par le store ; on confirme côté UI.
    const n = job.resultats?.n_factures ?? 0
    confirmation.value = `${n} facture(s) déposée(s) et ajoutée(s) à votre liste.`
}
</script>

<template>
    <div class="p-8">
        <header class="mb-6 flex items-start justify-between gap-3">
            <div>
                <h1 class="m-0 text-2xl font-medium text-primary-900">
                    Mes factures
                </h1>
                <p class="m-0 mt-1 text-sm text-neutral-600">
                    Recherchez et filtrez toutes vos factures
                </p>
            </div>
            <button
                type="button"
                class="inline-flex items-center gap-2 rounded-md bg-primary-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700"
                @click="onDeposer"
            >
                <i class="pi pi-upload" aria-hidden="true" />
                Déposer une facture
            </button>
        </header>

        <!-- Compteurs : une seule décision pour le client — « à vous » vs « à
             ajouter à votre compte » (le distinguo interne rattacher/créer reste
             côté back, invisible ici). -->
        <div v-if="agregats" class="mb-5 grid grid-cols-3 gap-3">
            <div class="rounded-lg bg-neutral-50 px-4 py-3">
                <div class="text-xs text-neutral-500">Factures</div>
                <div class="text-2xl font-medium text-primary-900">
                    {{ agregats.n_total }}
                </div>
            </div>
            <div class="rounded-lg bg-neutral-50 px-4 py-3">
                <div class="text-xs text-neutral-500">À vous</div>
                <div class="text-2xl font-medium text-green-700">
                    {{ agregats.n_visible }}
                </div>
            </div>
            <div class="rounded-lg bg-neutral-50 px-4 py-3">
                <div class="text-xs text-neutral-500">
                    À ajouter à votre compte
                </div>
                <div class="text-2xl font-medium text-primary-700">
                    {{ nbAVerrouiller }}
                </div>
            </div>
        </div>

        <!-- Légende : deux états vus du client -->
        <div class="mb-3 flex flex-wrap gap-4 text-xs text-neutral-500">
            <span class="inline-flex items-center gap-1.5">
                <span
                    class="inline-block h-2.5 w-2.5 rounded-sm bg-green-500"
                />
                à vous — vous détenez le compteur
            </span>
            <span class="inline-flex items-center gap-1.5">
                <span
                    class="inline-block h-2.5 w-2.5 rounded-sm bg-primary-500"
                />
                à ajouter — pas encore sur votre compte
            </span>
        </div>

        <div
            v-if="error"
            class="mb-4 flex items-center gap-2.5 rounded-md border-l-4 border-red-500 bg-red-50 px-5 py-3.5 text-sm text-red-700"
        >
            <i class="pi pi-exclamation-triangle" aria-hidden="true" />
            <span>{{ error }}</span>
        </div>

        <div
            v-if="confirmation"
            class="mb-4 flex items-center gap-2.5 rounded-md border-l-4 border-green-500 bg-green-50 px-5 py-3.5 text-sm text-green-800"
        >
            <i class="pi pi-check-circle" aria-hidden="true" />
            <span>{{ confirmation }}</span>
        </div>

        <FacturesFiltres v-model="query" :agregats="agregats" />

        <FacturesList
            :factures="factures"
            :loading="loading"
            :filtered="hasActiveFilters"
            @voir="onVoir"
            @cta="onCta"
        />

        <!-- Pagination curseur : charge la page suivante en l'ajoutant à la liste. -->
        <div v-if="nextCursor" class="mt-4 flex justify-center">
            <button
                type="button"
                :disabled="loadingMore"
                class="inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                @click="store.fetchMore()"
            >
                <i
                    :class="
                        loadingMore ? 'pi pi-spinner pi-spin' : 'pi pi-plus'
                    "
                    aria-hidden="true"
                />
                {{ loadingMore ? "Chargement…" : "Charger plus" }}
            </button>
        </div>

        <ModalAjouterCompte
            :model-value="aAjouter !== null"
            :facture="aAjouter"
            @update:model-value="
                (v) => {
                    if (!v) aAjouter = null
                }
            "
            @confirm="onConfirmAjout"
        />

        <ModalDeposerFactures v-model="deposerOuvert" @done="onDepotFini" />

        <p class="mt-4 text-xs leading-relaxed text-neutral-400">
            <i class="pi pi-shield" aria-hidden="true" />
            {{ nbAVerrouiller }} factures concernent des compteurs pas encore
            sur votre compte. Ajoutez-les pour voir leurs montants et leurs
            factures.
        </p>
    </div>
</template>
